/**
 * CreatePageDialog
 * Dialog for creating a new page with title, slug, and type selection.
 */

import { useState, useEffect } from 'react'
import { Loader2 } from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { useCreatePage } from '@/hooks/use-pages'
import {
  validatePageSlug,
  generateSlugFromTitle,
  type DynamicPageType,
} from '@limo/shared'

interface CreatePageDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  accountId: string
  onSuccess: (pageId: string) => void
}

const PAGE_TYPES: { value: DynamicPageType; label: string }[] = [
  { value: 'custom', label: 'Custom Page' },
  { value: 'home', label: 'Home Page' },
  { value: 'about', label: 'About Page' },
  { value: 'contact', label: 'Contact Page' },
  { value: 'fleet', label: 'Fleet Page' },
  { value: 'faq', label: 'FAQ Page' },
]

export function CreatePageDialog({
  open,
  onOpenChange,
  accountId,
  onSuccess,
}: CreatePageDialogProps) {
  const [title, setTitle] = useState('')
  const [slug, setSlug] = useState('')
  const [pageType, setPageType] = useState<DynamicPageType>('custom')
  const [slugError, setSlugError] = useState<string | null>(null)
  const [slugManuallyEdited, setSlugManuallyEdited] = useState(false)

  const createPage = useCreatePage()

  // Reset form when dialog opens/closes
  useEffect(() => {
    if (open) {
      setTitle('')
      setSlug('')
      setPageType('custom')
      setSlugError(null)
      setSlugManuallyEdited(false)
    }
  }, [open])

  // Auto-generate slug from title (if not manually edited)
  useEffect(() => {
    if (!slugManuallyEdited && title) {
      const generatedSlug = generateSlugFromTitle(title)
      setSlug(generatedSlug)
      setSlugError(validatePageSlug(generatedSlug, { allowEmpty: true }))
    }
  }, [title, slugManuallyEdited])

  // Validate slug on change
  const handleSlugChange = (value: string) => {
    const normalized = value.toLowerCase().replace(/[^a-z0-9-]/g, '')
    setSlug(normalized)
    setSlugManuallyEdited(true)
    setSlugError(validatePageSlug(normalized, { allowEmpty: true }))
  }

  // Handle form submit
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    // Validate
    if (!title.trim()) {
      return
    }

    const error = validatePageSlug(slug, { allowEmpty: pageType === 'home' })
    if (error) {
      setSlugError(error)
      return
    }

    try {
      const page = await createPage.mutateAsync({
        accountId,
        title: title.trim(),
        slug: slug,
        pageType,
        createDefaultSections: true,
      })
      onSuccess(page.id)
    } catch (err) {
      // Error is handled by the hook's onError
      console.error('Failed to create page:', err)
    }
  }

  const isSubmitDisabled =
    !title.trim() ||
    createPage.isPending ||
    (!!slugError && !(pageType === 'home' && slug === ''))

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>Create New Page</DialogTitle>
          <DialogDescription>
            Add a new page to your website. You can customize the sections and
            content after creating.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="page-title">Page Title</Label>
            <Input
              id="page-title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g., Privacy Policy"
              autoFocus
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="page-slug">URL Slug</Label>
            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground">/</span>
              <Input
                id="page-slug"
                value={slug}
                onChange={(e) => handleSlugChange(e.target.value)}
                placeholder="privacy-policy"
                className="flex-1"
              />
            </div>
            {slugError && (
              <p className="text-sm text-destructive">{slugError}</p>
            )}
            <p className="text-xs text-muted-foreground">
              This will be the URL path for your page
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="page-type">Page Template</Label>
            <Select
              value={pageType}
              onValueChange={(value: DynamicPageType) => setPageType(value)}
            >
              <SelectTrigger id="page-type">
                <SelectValue placeholder="Select a template" />
              </SelectTrigger>
              <SelectContent>
                {PAGE_TYPES.map((type) => (
                  <SelectItem key={type.value} value={type.value}>
                    {type.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">
              Templates include pre-configured sections you can customize
            </p>
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={isSubmitDisabled}>
              {createPage.isPending && (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              )}
              Create Page
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
